import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import PaginationContainer from "@plannel/components/PaginationContainer";
import scenarioService from "@plannel/services/ai/scenarioService";
import StatusChip from "./StatusChip";
import {
  featureLabel, fmtAmt, fmtInt, localTs, moduleLabel, operationLabel, statusLabel, userLabel,
} from "./llmUsageUtil";

const PAGE_SIZES = [50, 100, 200, 500];

const LlmUsageDetailTab = ({ viewName, filters, reloadKey, notify, toolbarEl }) => {
  const { t } = useTranslation();
  const gridRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const load = async (p = page, size = pageSize) => {
    try {
      const r = await scenarioService.llmUsageCalls({ ...filters, page: p - 1, size });
      setRows(r?.rows || []);
      setTotal(Number(r?.total || 0));
    } catch {
      setRows([]); setTotal(0);
      notify("error", t("agentTraceLoadFail"));
    }
  };

  useEffect(() => {
    setPage(1);
    load(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const changePage = (p) => { setPage(p); load(p, pageSize); };
  const changeSize = (size) => { setPageSize(size); load(1, size); };

  // 표준 ExcelExportButton 의 service 계약(getAllForCSV → data.results)에 맞춘 어댑터 — 조건 전체를 상한까지 받는다
  const exportService = useMemo(() => ({
    getAllForCSV: (params) => scenarioService.llmUsageCalls({ ...params, export: true }).then((r) => {
      if (r?.capped) notify("warning", t("llmUsageExportCapped", { max: fmtInt(r.rows?.length || 0) }));
      return { data: { results: r?.rows || [] } };
    }),
  }), [notify, t]);

  const columnDefs = useMemo(() => [
    { field: "call_ts", headerName: t("llmUsageCallTs"), width: 170,
      valueFormatter: (p) => localTs(p.value) },
    { field: "feature_cd", headerName: t("llmUsageFeature"), width: 150,
      valueFormatter: (p) => featureLabel(t, p.value) },
    { field: "module_cd", headerName: t("agentTraceModule"), width: 100,
      valueFormatter: (p) => moduleLabel(t, p.value) },
    { field: "label", headerName: t("llmUsageLabel"), width: 150 },
    // 다른 필드를 참조하는 열은 valueGetter — Excel 내보내기의 valueFormatter 호출에는 행 data 가 없다
    { colId: "user_id", headerName: t("user"), width: 200,
      valueGetter: (p) => userLabel(t, p.data?.user_id, p.data?.user_name) },
    { field: "provider_cd", headerName: t("llmUsageProvider"), width: 100 },
    { field: "model_id", headerName: t("agentTraceMetricModel"), width: 260 },
    { field: "model_nm", headerName: t("llmUsageModelName"), width: 160 },
    { field: "operation_cd", headerName: t("llmUsageOperation"), width: 120,
      valueFormatter: (p) => operationLabel(t, p.value) },
    { field: "input_tokens", headerName: t("llmUsageInputTokens"), type: ["numberColumn", "rightAligned"], width: 120 },
    { field: "output_tokens", headerName: t("llmUsageOutputTokens"), type: ["numberColumn", "rightAligned"], width: 120 },
    { field: "cache_read_tokens", headerName: t("llmUsageCacheReadTokens"), type: ["numberColumn", "rightAligned"], width: 130 },
    { field: "cache_write_tokens", headerName: t("llmUsageCacheWriteTokens"), type: ["numberColumn", "rightAligned"], width: 130 },
    { field: "call_cnt", headerName: t("llmUsageCalls"), type: ["numberColumn", "rightAligned"], width: 100 },
    { field: "currency_cd", headerName: t("currencyCd"), width: 100 },
    // 값은 숫자(정렬·엑셀), 화면 표시만 서식 — 단가 미등록 행은 값 없음으로 둔다
    { colId: "cost_amt", headerName: t("llmUsageEstCost"), width: 130, type: ["rightAligned"],
      valueGetter: (p) => (p.data?.priced_flg === "Y" ? Number(p.data?.cost_amt) : null),
      valueFormatter: (p) => (p.value == null ? t("llmUsageUnpriced") : fmtAmt(p.value)) },
    { field: "latency_ms", headerName: t("llmUsageLatency"), type: ["numberColumn", "rightAligned"], width: 120 },
    // valueFormatter 는 Excel 내보내기·정렬용 텍스트, 화면은 칩
    { field: "status_cd", headerName: t("status"), width: 100,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
      valueFormatter: (p) => statusLabel(t, p.value),
      cellRenderer: (p) => <StatusChip value={p.value} /> },
    { field: "error_msg", headerName: t("error"), width: 220, tooltipField: "error_msg" },
    { field: "session_id", headerName: t("llmUsageSession"), width: 200 },
    // 마지막 열이 남는 폭을 흡수한다(표준 마스터 화면의 flex 열과 같은 방식) — 좁아지면 minWidth 로 고정되고 가로 스크롤
    { field: "request_id", headerName: t("llmUsageRequest"), minWidth: 200, flex: 1 },
  ], [t]);

  const gridSetting = useMemo(() => {
    const base = DefaultGridSetting({ title: "menuAiLlmUsage", viewName, columnDefs });
    return { ...base, defaultColDef: { ...base.defaultColDef, editable: false } };
  }, [viewName, columnDefs]);

  return (
    <>
      {/* 포털은 Box 의 children propTypes 에 걸리므로 Box 밖에 둔다 */}
      {toolbarEl ? createPortal(
        <ExcelExportButton title="menuAiLlmUsage" service={exportService} params={filters}
          columnDefs={columnDefs} totalRows={total} />, toolbarEl) : null}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, height: "100%" }}>
        {/* .grid-area(height:100%) 는 flex:1 을 덮어 하단 페이지네이션을 밀어내므로 쓰지 않는다 */}
        <Box className="ag-theme-balham" sx={{ flex: 1, minHeight: 240 }}>
          <AgGridReact ref={gridRef} rowData={rows} columnDefs={columnDefs} {...gridSetting}
            tooltipShowDelay={0} />
        </Box>
        <PaginationContainer currentPage={page} totalPages={Math.max(1, Math.ceil(total / pageSize))}
          pageSize={pageSize} pageSizes={PAGE_SIZES} setCurrentPage={changePage} setPageSize={changeSize} />
      </Box>
    </>
  );
};

export default LlmUsageDetailTab;
